import {
 createContext,
 useContext,
 useState,
 ReactNode,
 useCallback,
} from "react";
import { nanoid } from "nanoid";
import { Toast, ToastType } from "types/Toast";

interface ToastContextType {
 addToast: (
  message: string | { key: string; data?: any },
  type?: ToastType,
  duration?: number
 ) => void;
 removeToast: (id: string) => void;
 toasts: Toast[];
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider = ({ children }: { children: ReactNode }) => {
 const [toasts, setToasts] = useState<Toast[]>([]);

 const removeToast = useCallback((id: string) => {
  setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
 }, []);

 const addToast = useCallback(
  (
   message: string | { key: string; data?: any },
   type: ToastType = "info",
   duration: number = 5000
  ) => {
   const id = nanoid();
   setToasts((prevToasts: Toast[]) => [
    ...prevToasts,
    { id, type, message: typeof message === "string" ? message : message.key, duration },
   ] as Toast[]);

   setTimeout(() => removeToast(id), duration);
  },
  [removeToast]
 );

 return (
  <ToastContext.Provider value={{ addToast, removeToast, toasts }}>
   {children}
  </ToastContext.Provider>
 );
};

export const useToast = () => {
 const context = useContext(ToastContext);
 if (!context) {
  throw new Error("useToast must be used within a ToastProvider");
 }
 return context;
};
